// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Users, MapPin, Phone, Mail } from 'lucide-react';
import { useReservation } from '../../contexts/ReservationContext.tsx';
import { useRestaurant } from '../../contexts/RestaurantContext.tsx';
import ReservationForm from '../../components/admin/ReservationForm.tsx';
import { buildWhatsappLink, WHATSAPP_ICON_URL } from '../../../../../utils/whatsapp';
import '../../styles/admin-dashboard.css';

const TodayReservations: React.FC = () => {
  const { reservations } = useReservation();
  const { floors } = useRestaurant();
  const [roomFilter, setRoomFilter] = useState('all');
  const [showReservationForm, setShowReservationForm] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<any>(null);

  const formatBirthdate = (isoDate?: string | null) => {
    if (!isoDate) return '';
    const [datePart] = isoDate.split('T');
    const parts = datePart?.split('-');
    if (!parts || parts.length !== 3) return isoDate;
    const [year, month, day] = parts.map(Number);
    if (!year || !month || !day) return isoDate;
    const parsed = new Date(year, month - 1, day);
    if (Number.isNaN(parsed.getTime())) return isoDate;
    return parsed.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const normalize = (res: any) => {
    const tableIds = Array.isArray(res.mesa_ids) && res.mesa_ids.length > 0
      ? res.mesa_ids
      : (res.mesa_id ? [res.mesa_id] : []);
    let tableNames = tableIds.map((mesaId: string | number) => `Mesa ${mesaId}`);
    let roomName = `Sala ${res.sala_id}`;

    if (floors && floors.length > 0) {
      const allTables = floors.flatMap(floor => floor.tables);
      const room = floors.find(floor => floor.id === res.sala_id?.toString());

      tableNames = tableIds.map((mesaId: string | number) => {
        const table = allTables.find(t => t.id === mesaId?.toString());
        return table ? table.name : `Mesa ${mesaId}`;
      });
      if (room) roomName = room.name;
    }

    // Parse date string ignoring time (format: YYYY-MM-DD) - same as calendar
    const [year, month, day] = res.data.split('-').map(Number);
    const resDate = new Date(year, month - 1, day);

    return {
      id: res.id,
      date: resDate,
      time: res.hora,
      name: res.nome_cliente,
      tableId: tableIds[0] ?? null,
      tableIds,
      tableName: tableNames.join(', '),
      roomName,
      guests: res.numero_pessoas,
      phone: res.telefone_cliente,
      whatsappLink: buildWhatsappLink(res.telefone_cliente),
      email: res.email_cliente,
      birthdate: res.nascimento_cliente,
      notes: res.obs,
      turno: res.turno || 0,
      sala_id: res.sala_id
    };
  };

  const normalized = reservations.map(normalize);

  // Get today's reservations
  const today = new Date();
  const todayReservations = normalized.filter(r => {
    return (
      r.date.getDate() === today.getDate() &&
      r.date.getMonth() === today.getMonth() &&
      r.date.getFullYear() === today.getFullYear()
    );
  }).sort((a, b) => {
    // Sort by time
    const aTime = parseInt(a.time.split(':')[0]) * 60 + parseInt(a.time.split(':')[1]);
    const bTime = parseInt(b.time.split(':')[0]) * 60 + parseInt(b.time.split(':')[1]);
    return aTime - bTime;
  });

  const filteredReservations = roomFilter === 'all'
    ? todayReservations
    : todayReservations.filter(r => String(r.sala_id) === String(roomFilter));

  // Group reservations by turno
  const groupedReservations = filteredReservations.reduce((acc, res) => {
    const turnoKey = res.turno;

    if (!acc[turnoKey]) {
      acc[turnoKey] = [];
    }
    acc[turnoKey].push(res);
    return acc;
  }, {} as Record<number, typeof todayReservations>);

  // Helper function to get turno name
  const getTurnoName = (turno: number) => {
    return turno === 0 ? 'Almoço' : turno === 1 ? 'Jantar' : `Turno ${turno}`;
  };

  const handleEditReservation = (reservationId: number | string) => {
    if (!reservationId) return;
    const reservation = reservations.find((r) => String(r.id) === String(reservationId));
    if (!reservation) return;
    setSelectedReservation(reservation);
    setShowReservationForm(true);
  };

  const handleCloseForm = () => {
    setShowReservationForm(false);
    setSelectedReservation(null);
  };

  const selectedReservationDate = selectedReservation?.data
    ? new Date(`${selectedReservation.data}T00:00:00`)
    : new Date();

  return (
    <div className="admin-dashboard">
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-xl)' }}>
        <Link to="/reservo" className="action-button" style={{ padding: 'var(--spacing-sm)', width: 'auto' }}>
          <ArrowLeft size={20} />
        </Link>
        <h1>Reservas de Hoje</h1>
      </div>

      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-icon">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <h3>Total de Reservas</h3>
            <p className="stat-value">{filteredReservations.length}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <h3>Total de Pessoas</h3>
            <p className="stat-value">
              {filteredReservations.reduce((total, res) => total + res.guests, 0)}
            </p>
          </div>
        </div>
      </div>

      <div className="recent-activity" style={{ marginBottom: 0 }}>
        <div className="room-today-header">
          <h2>Reservas do Dia</h2>
          <div className="room-today-filter">
            <label htmlFor="today-reservas-filter">Ambiente</label>
            <select
              id="today-reservas-filter"
              className="form-control"
              value={roomFilter}
              onChange={(e) => setRoomFilter(e.target.value)}
            >
              <option value="all">Todos os ambientes</option>
              {floors.map((floor) => (
                <option key={floor.id} value={String(floor.id)}>
                  {floor.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="activity-list">
          {Object.keys(groupedReservations).length > 0 ? (
            Object.entries(groupedReservations).map(([turnoKey, reservations]) => (
              <div key={turnoKey} style={{ marginBottom: 'var(--spacing-xl)' }}>
                <h3 style={{ marginBottom: 'var(--spacing-md)', color: 'var(--primary-600)' }}>
                  {getTurnoName(parseInt(turnoKey))}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                  {reservations.map(res => (
                    <div
                      key={res.id}
                      className="activity-item"
                      style={{ flexDirection: 'column', alignItems: 'stretch', gap: 'var(--spacing-sm)', cursor: 'pointer' }}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleEditReservation(res.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleEditReservation(res.id);
                        }
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div className="activity-time">
                          {res.time}
                        </div>
                        <div style={{ fontSize: 'var(--font-sm)', color: 'var(--neutral-600)' }}>
                          {res.guests} pessoa{res.guests !== 1 ? 's' : ''}
                        </div>
                      </div>

                      <div className="activity-content">
                        <div style={{ marginBottom: 'var(--spacing-xs)' }}>
                          <strong style={{ fontSize: 'var(--font-lg)' }}>{res.name}</strong>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)', color: 'var(--neutral-600)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                            <MapPin size={16} />
                            <span>{res.tableName ? `${res.tableName} - ` : ''}{res.roomName}</span>
                            {res.notes && (
                              <span className="reservation-notes-tooltip" title={res.notes}>Obs</span>
                            )}
                          </div>

                          {res.phone && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', flexWrap: 'wrap' }}>
                              <Phone size={16} />
                              <span>{res.phone}</span>
                              {res.whatsappLink && (
                                <a
                                  className="reservation-whatsapp-link"
                                  href={res.whatsappLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(event) => event.stopPropagation()}
                                  onKeyDown={(event) => event.stopPropagation()}
                                  aria-label={`Falar com ${res.name || 'cliente'} pelo WhatsApp`}
                                >
                                  <img
                                    className="reservation-whatsapp-icon"
                                    src={WHATSAPP_ICON_URL}
                                    alt=""
                                    aria-hidden="true"
                                  />
                                  <span>Falar com cliente</span>
                                </a>
                              )}
                            </div>
                          )}

                          {res.email && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                              <Mail size={16} />
                              <span>{res.email}</span>
                            </div>
                          )}

                          {res.birthdate && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                              <Calendar size={16} />
                              <span>Nascimento: {formatBirthdate(res.birthdate)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))) : (
            <div className="activity-item">
              <div className="activity-content">
                Nenhuma reserva para hoje
              </div>
            </div>
          )}
        </div>
      </div>

      {showReservationForm && selectedReservation && (
        <ReservationForm
          reservation={selectedReservation}
          selectedDate={selectedReservationDate}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};

export default TodayReservations;

